import { Box, Popover, SxProps } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSweepFields } from '../../../../sweep-fields/useCachedSweepFields';
import { colors, Checkbox, Typography } from '@sweep-io/sweep-design';
import { useRunOnceWhenTruthy } from '../../useRunOnceWhenTruthy';
import { DataTable } from '../../table/DataTable';
import { DataTableRow, DataTableVariant } from '../../table/TableTypes';
import { DataTableId } from '../../table/utils';

export interface PrioritizePicklistSelectorProps {
  shouldBeDisabled?: boolean;
  chosenValues?: string;
  field?: SweepField;
  onChange: (chosenValues: string) => void;
  crmOrgId: string;
  fieldIds?: string[];
  sx?: SxProps;
  placeholder?: string;
  removeBorders?: boolean;
}

const SEPARATOR = '; ';

interface PicklistRow extends DataTableRow {
  id: string;
  checkbox: JSX.Element;
  label: string;
  isChecked: boolean;
}

const columns = [
  { field: 'checkbox', headerName: '', width: '50px' },
  { field: 'label', headerName: '', width: 'auto' },
];

type PicklistValue = { fullName: string; label: string; isChecked: boolean };

const getSelectedValuesString = (
  values: PicklistValue[],
  key: 'fullName' | 'label',
  separator: string,
) =>
  values
    .filter((v) => v.isChecked)
    .map((v) => v[key])
    .join(separator);

const PrioritizePicklistSelector = ({
  shouldBeDisabled,
  chosenValues,
  field: providedField,
  onChange,
  fieldIds,
  sx,
  placeholder = 'Set value',
  removeBorders,
  crmOrgId,
}: PrioritizePicklistSelectorProps) => {
  const { getSweepFieldsById } = useSweepFields();
  const [field, setField] = useState<SweepField | undefined>(providedField);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fieldIdToFetch = fieldIds?.[fieldIds ? fieldIds.length - 1 : 0];
  const valueSet = field?.properties?.valueSet as PicklistValueSetItem[] | undefined;

  const chosenValuesArray = useMemo(
    () => (chosenValues ? chosenValues.split(SEPARATOR) : []),
    [chosenValues],
  );

  // Build ordered list: checked items first (in their saved priority order), then unchecked items (in Salesforce default order).
  // Note: This logic intentionally iterates chosenValuesArray first to preserve the user's priority order.
  // A simpler valueSet.map() approach would lose the saved ordering.
  const getOrderedValues = useCallback((): PicklistValue[] => {
    if (!valueSet) return [];

    const checkedItems = chosenValuesArray
      .map((fullName) => valueSet.find((v) => v.fullName === fullName))
      .filter(Boolean)
      .map((v) => ({ fullName: v!.fullName, label: v!.label, isChecked: true }));

    const uncheckedItems = valueSet
      .filter((v) => !chosenValuesArray.includes(v.fullName))
      .map((v) => ({ fullName: v.fullName, label: v.label, isChecked: false }));

    return [...checkedItems, ...uncheckedItems];
  }, [valueSet, chosenValuesArray]);

  const [orderedValues, setOrderedValues] = useState<PicklistValue[]>([]);

  useEffect(() => {
    setOrderedValues(getOrderedValues());
  }, [getOrderedValues]);

  // Fetch field if not provided
  const fetchAndUpdateField = useCallback(
    async ({ fieldIds }: { fieldIds: string[] }) => {
      setIsLoading(true);
      const lastIdx = fieldIds.length - 1;
      const fields = await getSweepFieldsById({
        fieldIds: [fieldIds[lastIdx]],
        crmOrgId,
      });
      const relevantField = fields[0];
      if (relevantField) {
        setField(relevantField);
      }
      setIsLoading(false);
    },
    [getSweepFieldsById, crmOrgId],
  );

  useRunOnceWhenTruthy(() => {
    fetchAndUpdateField({ fieldIds: [fieldIdToFetch!] });
  }, !!fieldIdToFetch && !providedField);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!shouldBeDisabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleCheckChange = (fullName: string, checked: boolean) => {
    const newOrderedValues = orderedValues.map((v) =>
      v.fullName === fullName ? { ...v, isChecked: checked } : v,
    );
    setOrderedValues(newOrderedValues);
    onChange(getSelectedValuesString(newOrderedValues, 'fullName', SEPARATOR));
  };

  const handleReorder = ({
    sourceIndex,
    destinationIndex,
  }: {
    sourceIndex: number;
    destinationIndex: number;
  }) => {
    const newOrderedValues = [...orderedValues];
    const [removed] = newOrderedValues.splice(sourceIndex, 1);
    newOrderedValues.splice(destinationIndex, 0, removed);
    setOrderedValues(newOrderedValues);
    onChange(getSelectedValuesString(newOrderedValues, 'fullName', SEPARATOR));
  };

  const rows: PicklistRow[] = orderedValues.map((value) => ({
    id: value.fullName,
    isChecked: value.isChecked,
    checkbox: (
      <Checkbox
        checked={value.isChecked}
        onChange={(e) => handleCheckChange(value.fullName, e.target.checked)}
      />
    ),
    label: value.label,
  }));

  // Display value for the button
  const displayValue =
    chosenValuesArray.length > 0 ? getSelectedValuesString(orderedValues, 'label', ', ') : '';

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          minHeight: '40px',
          padding: '8px 12px',
          borderRadius: '4px',
          border: '1px solid',
          borderColor: removeBorders ? 'transparent' : colors.grey[300],
          backgroundColor: shouldBeDisabled ? colors.grey[100] : 'white',
          cursor: shouldBeDisabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          '&:hover': {
            borderColor: shouldBeDisabled ? colors.grey[300] : colors.blue[500],
          },
          ...sx,
        }}
      >
        <Typography
          variant="body"
          color={displayValue ? colors.grey[900] : colors.grey[500]}
          ellipsis
        >
          {displayValue || placeholder}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
              maxHeight: '300px',
              overflow: 'auto',
              width: anchorEl?.offsetWidth ?? 'auto',
              minWidth: '300px',
            },
          },
        }}
      >
        <Box sx={{ width: '100%', p: 1 }}>
          {isLoading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body" color={colors.grey[500]}>
                Loading...
              </Typography>
            </Box>
          ) : orderedValues.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body" color={colors.grey[500]}>
                No picklist values available
              </Typography>
            </Box>
          ) : (
            <DataTable
              tableId={DataTableId.PrioritizePicklistTable}
              allowRowReorder={true}
              columns={columns}
              rows={rows}
              variant={DataTableVariant.small}
              onRowOrderChange={handleReorder}
              sx={{
                '& thead': {
                  display: 'none',
                },
                width: '100%',
              }}
              sxRowFunction={() => ({
                height: '40px',
                '& td.SweepDataTableCell.MuiTableCell-root': {
                  height: '40px',
                  padding: '8px 12px',
                },
                '& td.SweepDataTableCell.MuiTableCell-root:first-of-type': {
                  paddingLeft: '8px !important',
                  width: '72px',
                  minWidth: '72px',
                },
              })}
            />
          )}
        </Box>
      </Popover>
    </>
  );
};

export { PrioritizePicklistSelector };
